import type { ComponentType } from 'react';
import type { BlockContent, BlockStyle } from '@/blocks/domain/types';
import type { DocumentContext } from './DocumentContext';

export type FlowBehavior = 'atomic' | 'splittable';

export interface RendererViewport {
  /** When true, table renderers use auto layout and avoid letter-by-letter wrapping. Editor-only. */
  compact: boolean;
}

export interface RendererProps<C extends BlockContent = BlockContent> {
  content: C;
  style: BlockStyle;
  context: DocumentContext;
  accentColor?: string;
  slice?: SliceInfo;
  knownPaths?: ReadonlySet<string>;
  viewport?: RendererViewport;
}

export interface SliceInfo {
  startIndex: number;
  endIndex: number;
  isContinuation: boolean;
}

export interface SplittableRenderer<C extends BlockContent = BlockContent> extends Renderer<C> {
  flowBehavior: 'splittable';
  Header: ComponentType<RendererProps<C>>;
}

export interface Renderer<C extends BlockContent = BlockContent> {
  Component: ComponentType<RendererProps<C>>;
  flowBehavior: FlowBehavior;
  /** When true, paginate (Part 4) should force following content onto a new page. */
  pageBreakAfter?: boolean;
}

export type RendererContract = Renderer;

export const isSplittable = (r: Renderer): r is SplittableRenderer => r.flowBehavior === 'splittable';
